/**
 * Pictures and cards, kept on this device (db.ts) - the same calls, answers and
 * refusals the server's API gave until 2026-10-10, so the pages did not have to
 * change: a missing card is a 404, a save over a newer one is a 409.
 *
 * Pictures are shown from blob: addresses made from the stored files. Each one
 * is made once and kept for as long as the page is open (they cost nothing
 * until drawn), and let go when the picture is deleted or the account changes.
 */
import { emptyProjectDocument, type ProjectDocument, type TemplateDocument } from '@foldmycard/scene';
import { ApiError } from '../api-error.ts';
import type { ImageItem, ProjectItem, Size, VersionItem } from '../api.ts';
import { cartoonOf } from './cartoon.ts';
import { keepStorage, onAccountChange, req, tx } from './db.ts';
import { blackWhiteOf, decode, drawScaled, fit, preparePicture, SIZES, toJpeg, type PreparedFile, type SizeKind } from './pictures.ts';

/** A picture as it is stored: its sizes, no pixels (those are in "files"). */
export interface PictureRow {
  id: string; name: string | null; width: number; height: number; createdAt: string;
  sizes: Record<SizeKind, Size>;
  cartoon: Record<SizeKind, Size> | null;
  /** The black-and-white look, once made (2026-10-10). */
  bw?: Record<SizeKind, Size> | null;
  /**
   * The card this picture belongs to: each card has its own pictures and shows
   * only those, so cards never mix (2026-10-10). Absent: added from the home
   * page before then - given to the card that shows it (adoptPictures), or
   * left over if no card does.
   */
  projectId?: string | null;
  /** Taken from the picture library (library/index.ts): the item's id there. */
  library?: string;
  /** Copied from another card's picture (copyPictures): the first picture's id, so it is never copied twice. */
  copiedFrom?: string;
}
export interface ProjectRow { id: string; title: string; templateId: string; paper: string; createdAt: string; updatedAt: string }
export interface VersionRow {
  id: string; projectId: string; label: string; basedOn: string | null; revision: number; templateHash: string;
  document: ProjectDocument; createdAt: string; updatedAt: string;
}

export const FILE_KINDS = ['thumb', 'preview', 'cartoon-thumb', 'cartoon-preview', 'bw-thumb', 'bw-preview'] as const;
export type FileKind = typeof FILE_KINDS[number];
export const fileKey = (id: string, kind: FileKind) => `${id}/${kind}`;

const notFound = () => new ApiError(404, { error: 'not_found' });

/* ------------------------------------------------------------- card events */

/** A card was added, changed or deleted on this device - the card folder (folder.ts) listens. */
export type CardEvent = { kind: 'changed' | 'deleted'; projectId: string };
const cardListeners = new Set<(e: CardEvent) => void>();
export const onCardEvent = (f: (e: CardEvent) => void) => { cardListeners.add(f); return () => { cardListeners.delete(f); }; };
const announce = (e: CardEvent) => { for (const f of cardListeners) f(e); };
const now = () => new Date().toISOString();
const uuid = () => crypto.randomUUID();

/* --------------------------------------------------------- picture addresses */

const urls = new Map<string, string>();
onAccountChange(() => {
  for (const u of urls.values()) URL.revokeObjectURL(u);
  urls.clear();
});

async function urlsFor(rows: PictureRow[]): Promise<Map<string, string>> {
  const missing = rows.flatMap((r) => FILE_KINDS.filter((k) => (k.startsWith('cartoon') ? r.cartoon : k.startsWith('bw') ? r.bw : true))
    .map((k) => fileKey(r.id, k))).filter((k) => !urls.has(k));
  if (missing.length) {
    const blobs = await tx(['files'], 'readonly', (t) => Promise.all(missing.map((k) => req(t.objectStore('files').get(k)))));
    missing.forEach((k, i) => { if (blobs[i]) urls.set(k, URL.createObjectURL(blobs[i] as Blob)); });
  }
  return urls;
}

function imageItem(r: PictureRow, u: Map<string, string>): ImageItem {
  const at = (prefix: string) => ({ thumb: u.get(fileKey(r.id, `${prefix}thumb` as FileKind)) ?? '',
    preview: u.get(fileKey(r.id, `${prefix}preview` as FileKind)) ?? '' });
  return {
    id: r.id, name: r.name, width: r.width, height: r.height, createdAt: r.createdAt,
    sizes: r.sizes, urls: at(''),
    cartoon: r.cartoon ? { sizes: r.cartoon, urls: at('cartoon-') } : null,
    bw: r.bw ? { sizes: r.bw, urls: at('bw-') } : null,
    projectId: r.projectId ?? null, library: r.library ?? null, copiedFrom: r.copiedFrom ?? null,
  };
}

/* ----------------------------------------------------------------- pictures */

/** Which cards show each picture, in any version: picture id -> card ids. */
async function shownBy(t: IDBTransaction): Promise<Map<string, Set<string>>> {
  const m = new Map<string, Set<string>>();
  for (const v of await req(t.objectStore('versions').getAll() as IDBRequest<VersionRow[]>)) {
    for (const s of Object.values(v.document.zones)) {
      if (!s.image?.id) continue;
      if (!m.has(s.image.id)) m.set(s.image.id, new Set());
      m.get(s.image.id)!.add(v.projectId);
    }
  }
  return m;
}

/**
 * Pictures added before each card had its own (no card, or a deleted one) go
 * to the card that shows them - the most recently edited one, if several do.
 * Those no card shows are left over: the home page lists them, to delete.
 */
let adopted = false;
onAccountChange(() => { adopted = false; });
async function adoptPictures(): Promise<void> {
  if (adopted) return;
  await tx(['projects', 'versions', 'pictures'], 'readwrite', async (t) => {
    const rows = await req(t.objectStore('pictures').getAll() as IDBRequest<PictureRow[]>);
    const cards = new Map((await req(t.objectStore('projects').getAll() as IDBRequest<ProjectRow[]>)).map((p) => [p.id, p]));
    // also those of a card deleted before a card's pictures went with it
    const loose = rows.filter((r) => !r.projectId || !cards.has(r.projectId));
    if (!loose.length) return;
    const shown = await shownBy(t);
    for (const r of loose) {
      const owner = [...(shown.get(r.id) ?? [])].map((id) => cards.get(id)).filter((p): p is ProjectRow => !!p)
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
      if (owner) await req(t.objectStore('pictures').put({ ...r, projectId: owner.id }));
    }
  });
  adopted = true;
}

export async function images(): Promise<{ images: ImageItem[] }> {
  await adoptPictures();
  const rows = await tx(['pictures'], 'readonly', (t) => req(t.objectStore('pictures').getAll() as IDBRequest<PictureRow[]>));
  rows.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const u = await urlsFor(rows);
  return { images: rows.map((r) => imageItem(r, u)) };
}

/** Write a picture's row and files. `files` kinds without a prefix are the photograph's own. */
export async function putPicture(row: PictureRow, files: { kind: FileKind; blob: Blob }[]): Promise<void> {
  await tx(['pictures', 'files'], 'readwrite', async (t) => {
    for (const f of files) await req(t.objectStore('files').put(f.blob, fileKey(row.id, f.kind)));
    await req(t.objectStore('pictures').put(row));
  });
}

let askedToKeep = false;
/** Once a page has something worth keeping, ask the browser to keep it (db.ts keepStorage). */
const askToKeep = () => { if (!askedToKeep) { askedToKeep = true; void keepStorage(); } };

/** A card's pictures changed: it counts as edited (its card file, which carries them, is saved again). */
async function pictureOf(projectId: string | null | undefined) {
  if (!projectId) return;
  const found = await tx(['projects'], 'readwrite', async (t) => {
    const p = await getProject(t, projectId);
    if (p) await touch(t, p);
    return !!p;
  });
  if (found) announce({ kind: 'changed', projectId });
}

/** Something a card carries but does not hold changed - its event (events.ts): the card counts as edited, and its file is saved again. */
export const cardChanged = (projectId: string) => pictureOf(projectId);

/** Add a picture from a file the person picked: prepared here (pictures.ts), kept here - with the card it was added to. */
export async function upload(file: File, projectId: string | null = null): Promise<ImageItem> {
  const p = await preparePicture(file);
  const sizes = Object.fromEntries(p.files.map((f) => [f.kind, { width: f.width, height: f.height }])) as Record<SizeKind, Size>;
  const row: PictureRow = { id: uuid(), name: file.name.slice(0, 200) || null, width: p.width, height: p.height,
    createdAt: now(), sizes, cartoon: null, projectId };
  await putPicture(row, p.files);
  askToKeep();
  await pictureOf(projectId);
  return imageItem(row, await urlsFor([row]));
}

/**
 * Another card's pictures, for this one (2026-10-11): copied - with their
 * cartoon and black-and-white looks - so each card still has its own, and
 * deleting a card never takes another's pictures. One this card already has
 * (or a copy of it) is not copied again.
 */
export async function copyPictures(ids: string[], projectId: string): Promise<ImageItem[]> {
  const made = await tx(['pictures', 'files'], 'readwrite', async (t) => {
    const pictures = t.objectStore('pictures'), files = t.objectStore('files');
    const all = await req(pictures.getAll() as IDBRequest<PictureRow[]>);
    const have = new Set(all.filter((r) => r.projectId === projectId).flatMap((r) => (r.copiedFrom ? [r.id, r.copiedFrom] : [r.id])));
    const out: PictureRow[] = [];
    for (const id of ids) {
      const src = all.find((r) => r.id === id);
      const first = src?.copiedFrom ?? id;
      if (!src || have.has(id) || have.has(first)) continue;
      const row: PictureRow = { ...src, id: uuid(), projectId, createdAt: now(), copiedFrom: first };
      for (const kind of FILE_KINDS) {
        const blob = await req(files.get(fileKey(src.id, kind)) as IDBRequest<Blob | undefined>);
        if (blob) await req(files.put(blob, fileKey(row.id, kind)));
      }
      await req(pictures.put(row));
      have.add(id);
      have.add(first);
      out.push(row);
    }
    return out;
  });
  if (made.length) await pictureOf(projectId);
  const u = await urlsFor(made);
  return made.map((r) => imageItem(r, u));
}

async function picture(id: string): Promise<PictureRow> {
  const row = await tx(['pictures'], 'readonly', (t) => req(t.objectStore('pictures').get(id) as IDBRequest<PictureRow | undefined>));
  if (!row) throw notFound();
  return row;
}

/** Make (or return the already made) cartoon look of a picture: a few seconds the first time. */
export async function cartoon(id: string): Promise<ImageItem> {
  const row = await picture(id);
  if (!row.cartoon) {
    const preview = await tx(['files'], 'readonly', (t) => req(t.objectStore('files').get(fileKey(id, 'preview')) as IDBRequest<Blob | undefined>));
    if (!preview) throw notFound();
    const made: PreparedFile[] = await cartoonOf(preview);
    row.cartoon = Object.fromEntries(made.map((f) => [f.kind, { width: f.width, height: f.height }])) as Record<SizeKind, Size>;
    await putPicture(row, made.map((f) => ({ kind: `cartoon-${f.kind}` as FileKind, blob: f.blob })));
  }
  return imageItem(row, await urlsFor([row]));
}

/** Make (or return the already made) black-and-white look of a picture. */
export async function blackWhite(id: string): Promise<ImageItem> {
  const row = await picture(id);
  if (!row.bw) {
    const preview = await tx(['files'], 'readonly', (t) => req(t.objectStore('files').get(fileKey(id, 'preview')) as IDBRequest<Blob | undefined>));
    if (!preview) throw notFound();
    const made = await blackWhiteOf(preview);
    row.bw = Object.fromEntries(made.map((f) => [f.kind, { width: f.width, height: f.height }])) as Record<SizeKind, Size>;
    await putPicture(row, made.map((f) => ({ kind: `bw-${f.kind}` as FileKind, blob: f.blob })));
  }
  return imageItem(row, await urlsFor([row]));
}

/**
 * A drawing from the picture library, as a picture of this card: drawn at
 * the preview size onto white and kept like any other picture, so it can be
 * placed, turned into a cartoon or black and white, and saved in card files.
 * Picking the same drawing again for the same card reuses it.
 */
export async function addFromLibrary(item: { id: string; name: string; url: string }, projectId: string | null): Promise<ImageItem> {
  const all = await tx(['pictures'], 'readonly', (t) => req(t.objectStore('pictures').getAll() as IDBRequest<PictureRow[]>));
  const same = all.find((r) => r.library === item.id && (r.projectId ?? null) === projectId);
  if (same) return imageItem(same, await urlsFor([same]));
  // (decoded as a picture file is: see pictures.ts decode)
  const img = await decode(await (await fetch(item.url)).blob());
  // a drawing has no pixels of its own: give it the preview size on its long side;
  // a photograph (the administrator's, library.ts) is never made larger than it is
  const w0 = img.naturalWidth || 800, h0 = img.naturalHeight || 800;
  const drawing = item.url.endsWith('.svg') || item.url.startsWith('data:image/svg');
  const k = drawing ? SIZES.preview / Math.max(w0, h0) : Math.min(1, SIZES.preview / Math.max(w0, h0));
  const W = Math.round(w0 * k), H = Math.round(h0 * k);
  const big = drawScaled(img, W, H, W, H);
  const files: PreparedFile[] = [];
  for (const kind of ['preview', 'thumb'] as SizeKind[]) {
    const s = fit(W, H, SIZES[kind]);
    files.push({ kind, blob: await toJpeg(s.width === W ? big : drawScaled(big, W, H, s.width, s.height), 0.9), ...s });
  }
  big.width = 0;
  const sizes = Object.fromEntries(files.map((f) => [f.kind, { width: f.width, height: f.height }])) as Record<SizeKind, Size>;
  const row: PictureRow = { id: uuid(), name: item.name, width: W, height: H, createdAt: now(), sizes, cartoon: null,
    projectId, library: item.id };
  await putPicture(row, files);
  askToKeep();
  await pictureOf(projectId);
  return imageItem(row, await urlsFor([row]));
}

/** The cards (and their titles) that show a picture in any version. */
async function cardsUsing(id: string): Promise<{ id: string; title: string }[]> {
  return tx(['projects', 'versions'], 'readonly', async (t) => {
    const out: { id: string; title: string }[] = [];
    for (const pid of (await shownBy(t)).get(id) ?? []) {
      const p = await getProject(t, pid);
      if (p) out.push({ id: p.id, title: p.title });
    }
    return out;
  });
}

/** A picture's row and files, in a transaction on "pictures" and "files". */
async function dropPicture(t: IDBTransaction, id: string) {
  for (const k of FILE_KINDS) await req(t.objectStore('files').delete(fileKey(id, k)));
  await req(t.objectStore('pictures').delete(id));
}
/** Let go of the addresses of deleted pictures. */
function forget(ids: string[]) {
  for (const id of ids) {
    for (const k of FILE_KINDS) {
      const u = urls.get(fileKey(id, k));
      if (u) { URL.revokeObjectURL(u); urls.delete(fileKey(id, k)); }
    }
  }
}

export async function deleteImage(id: string): Promise<null> {
  const row = await picture(id);
  const using = await cardsUsing(id);
  if (using.length) {
    throw new ApiError(409, { error: 'image_in_use', message: 'This picture is used in a card. Remove it from the card first.', projects: using });
  }
  await tx(['pictures', 'files'], 'readwrite', (t) => dropPicture(t, id));
  forget([id]);
  await pictureOf(row.projectId);
  return null;
}

/* -------------------------------------------------------------------- cards */

const projectItem = (p: ProjectRow, versions?: number): ProjectItem => ({ ...p, ...(versions !== undefined ? { versions } : {}) });
function versionItem(v: VersionRow, withDocument = false): VersionItem {
  const { projectId: _, document, ...rest } = v;
  return withDocument ? { ...rest, document } : rest;
}
const byProject = (t: IDBTransaction, pid: string) =>
  req(t.objectStore('versions').index('project').getAll(pid) as IDBRequest<VersionRow[]>);
const getProject = (t: IDBTransaction, id: string) => req(t.objectStore('projects').get(id) as IDBRequest<ProjectRow | undefined>);
/** "Version B" after "Version A", and so on. */
export const nextLabel = (n: number) => (n < 26 ? `Version ${String.fromCharCode(65 + n)}` : `Version ${n + 1}`);

export async function projects(): Promise<{ projects: ProjectItem[] }> {
  return tx(['projects', 'versions'], 'readonly', async (t) => {
    const rows = await req(t.objectStore('projects').getAll() as IDBRequest<ProjectRow[]>);
    const counts = new Map<string, number>();
    for (const v of await req(t.objectStore('versions').getAll() as IDBRequest<VersionRow[]>)) {
      counts.set(v.projectId, (counts.get(v.projectId) ?? 0) + 1);
    }
    rows.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    return { projects: rows.map((p) => projectItem(p, counts.get(p.id) ?? 0)) };
  });
}

/** A new card on the given (current) template: one empty version, "Version A". */
export async function createProject(title: string, template: TemplateDocument): Promise<{ project: ProjectItem; version: VersionItem }> {
  const at = now();
  const project: ProjectRow = { id: uuid(), title: title.trim().slice(0, 120) || 'Untitled card',
    templateId: template.templateId, paper: template.paper.slug, createdAt: at, updatedAt: at };
  const version: VersionRow = { id: uuid(), projectId: project.id, label: 'Version A', basedOn: null, revision: 1,
    templateHash: template.geometryHash, document: emptyProjectDocument(template), createdAt: at, updatedAt: at };
  await tx(['projects', 'versions'], 'readwrite', async (t) => {
    await req(t.objectStore('projects').add(project));
    await req(t.objectStore('versions').add(version));
  });
  askToKeep();
  announce({ kind: 'changed', projectId: project.id });
  return { project: projectItem(project), version: versionItem(version, true) };
}

export async function project(id: string): Promise<{ project: ProjectItem; versions: VersionItem[] }> {
  return tx(['projects', 'versions'], 'readonly', async (t) => {
    const p = await getProject(t, id);
    if (!p) throw notFound();
    const versions = (await byProject(t, id)).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    return { project: projectItem(p), versions: versions.map((v) => versionItem(v)) };
  });
}

export async function renameProject(id: string, title: string): Promise<{ project: ProjectItem }> {
  if (!title.trim()) throw new ApiError(400, { error: 'title_required' });
  const r = await tx(['projects'], 'readwrite', async (t) => {
    const p = await getProject(t, id);
    if (!p) throw notFound();
    const next = { ...p, title: title.trim().slice(0, 120), updatedAt: now() };
    await req(t.objectStore('projects').put(next));
    return { project: projectItem(next) };
  });
  announce({ kind: 'changed', projectId: id });
  return r;
}

/**
 * A card, all its versions, and its pictures - each card has its own. A
 * picture another card also shows (one added before cards had their own)
 * goes to that card instead.
 */
export async function deleteProject(id: string): Promise<null> {
  const gone: string[] = [];
  await tx(['projects', 'versions', 'pictures', 'files'], 'readwrite', async (t) => {
    if (!(await getProject(t, id))) throw notFound();
    for (const v of await byProject(t, id)) await req(t.objectStore('versions').delete(v.id));
    await req(t.objectStore('projects').delete(id));
    const shown = await shownBy(t);      // by the cards that are left
    for (const r of await req(t.objectStore('pictures').getAll() as IDBRequest<PictureRow[]>)) {
      if (r.projectId !== id) continue;
      const other = [...(shown.get(r.id) ?? [])][0];
      if (other) await req(t.objectStore('pictures').put({ ...r, projectId: other }));
      else { await dropPicture(t, r.id); gone.push(r.id); }
    }
  });
  forget(gone);
  announce({ kind: 'deleted', projectId: id });
  return null;
}

async function touch(t: IDBTransaction, p: ProjectRow) {
  await req(t.objectStore('projects').put({ ...p, updatedAt: now() }));
}

export async function version(pid: string, vid: string): Promise<{ version: VersionItem }> {
  return tx(['versions'], 'readonly', async (t) => {
    const v = await req(t.objectStore('versions').get(vid) as IDBRequest<VersionRow | undefined>);
    if (!v || v.projectId !== pid) throw notFound();
    return { version: versionItem(v, true) };
  });
}

/**
 * Save a version. `revision` is the one it was edited from; if another tab
 * saved in between, this refuses (409) instead of silently overwriting it.
 */
export async function saveVersion(pid: string, vid: string, document: ProjectDocument, revision: number): Promise<{ version: VersionItem }> {
  const r = await tx(['projects', 'versions'], 'readwrite', async (t) => {
    const p = await getProject(t, pid);
    const v = await req(t.objectStore('versions').get(vid) as IDBRequest<VersionRow | undefined>);
    if (!p || !v || v.projectId !== pid) throw notFound();
    if (v.revision !== revision) throw new ApiError(409, { error: 'revision_conflict', revision: v.revision });
    const next: VersionRow = { ...v, document, revision: v.revision + 1, updatedAt: now() };
    await req(t.objectStore('versions').put(next));
    await touch(t, p);
    return { version: versionItem(next, true) };
  });
  announce({ kind: 'changed', projectId: pid });
  return r;
}

/**
 * A new version copied from `from`: an independent copy from then on. `label` names it (else
 * the next letter); `document`, when given, is what the copy starts as instead of `from`'s saved
 * card - changes not saved yet, kept as a new version while `from` stays as it was.
 */
export async function duplicateVersion(pid: string, from: string,
  opts: { label?: string; document?: ProjectDocument } = {}): Promise<{ version: VersionItem }> {
  const r = await tx(['projects', 'versions'], 'readwrite', async (t) => {
    const p = await getProject(t, pid);
    const src = await req(t.objectStore('versions').get(from) as IDBRequest<VersionRow | undefined>);
    if (!p || !src || src.projectId !== pid) throw notFound();
    const n = (await byProject(t, pid)).length;
    const at = now();
    const label = opts.label?.trim().slice(0, 60) || nextLabel(n);
    const v: VersionRow = { id: uuid(), projectId: pid, label, basedOn: src.id, revision: 1,
      templateHash: src.templateHash, document: structuredClone(opts.document ?? src.document), createdAt: at, updatedAt: at };
    await req(t.objectStore('versions').add(v));
    await touch(t, p);
    return { version: versionItem(v, true) };
  });
  announce({ kind: 'changed', projectId: pid });
  return r;
}

/** A version's name. */
export async function renameVersion(pid: string, vid: string, label: string): Promise<{ version: VersionItem }> {
  const name = label.trim().slice(0, 60);
  if (!name) throw new ApiError(400, { error: 'empty_label', message: 'A version needs a name.' });
  const r = await tx(['projects', 'versions'], 'readwrite', async (t) => {
    const p = await getProject(t, pid);
    const v = await req(t.objectStore('versions').get(vid) as IDBRequest<VersionRow | undefined>);
    if (!p || !v || v.projectId !== pid) throw notFound();
    const next: VersionRow = { ...v, label: name };
    await req(t.objectStore('versions').put(next));
    return { version: versionItem(next, false) };
  });
  announce({ kind: 'changed', projectId: pid });
  return r;
}

/** Delete a version; a card keeps at least one. */
export async function deleteVersion(pid: string, vid: string): Promise<null> {
  await tx(['projects', 'versions'], 'readwrite', async (t) => {
    const p = await getProject(t, pid);
    const all = await byProject(t, pid);
    if (!p || !all.some((v) => v.id === vid)) throw notFound();
    if (all.length <= 1) throw new ApiError(409, { error: 'last_version', message: 'A card keeps at least one version.' });
    await req(t.objectStore('versions').delete(vid));
    await touch(t, p);
  });
  announce({ kind: 'changed', projectId: pid });
  return null;
}

/* ----------------------------------------------------- for card files (card-file.ts) */

/** Everything a card is: its row, every version, and its pictures - its own, and any its versions show (rows and files). */
export async function wholeCard(pid: string) {
  return tx(['projects', 'versions', 'pictures', 'files'], 'readonly', async (t) => {
    const p = await getProject(t, pid);
    if (!p) throw notFound();
    const versions = (await byProject(t, pid)).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    const ids = new Set(versions.flatMap((v) => Object.values(v.document.zones).map((s) => s.image?.id).filter((x): x is string => !!x)));
    for (const r of await req(t.objectStore('pictures').getAll() as IDBRequest<PictureRow[]>)) if (r.projectId === pid) ids.add(r.id);
    const pictures: { row: PictureRow; files: { kind: FileKind; blob: Blob }[] }[] = [];
    for (const id of ids) {
      const row = await req(t.objectStore('pictures').get(id) as IDBRequest<PictureRow | undefined>);
      if (!row) continue;
      const files: { kind: FileKind; blob: Blob }[] = [];
      for (const kind of FILE_KINDS) {
        const blob = await req(t.objectStore('files').get(fileKey(id, kind)) as IDBRequest<Blob | undefined>);
        if (blob) files.push({ kind, blob });
      }
      pictures.push({ row, files });
    }
    return { project: p, versions, pictures };
  });
}

export interface WholeCard {
  project: ProjectRow; versions: VersionRow[];
  pictures: { row: PictureRow; files: { kind: FileKind; blob: Blob }[] }[];
}

/**
 * Put a card read from a card file into this device's store. A card with the
 * same id is replaced - all its versions - by this one; pictures already here
 * are kept as they are (the same id is the same picture).
 */
export async function putWholeCard(card: WholeCard) {
  await tx(['projects', 'versions', 'pictures', 'files'], 'readwrite', async (t) => {
    for (const pic of card.pictures) {
      if (await req(t.objectStore('pictures').getKey(pic.row.id))) continue;
      for (const f of pic.files) await req(t.objectStore('files').put(f.blob, fileKey(pic.row.id, f.kind)));
      // a picture new to this device belongs with the card it came in
      await req(t.objectStore('pictures').put({ ...pic.row, projectId: pic.row.projectId ?? card.project.id }));
    }
    for (const v of await byProject(t, card.project.id)) await req(t.objectStore('versions').delete(v.id));
    await req(t.objectStore('projects').put(card.project));
    for (const v of card.versions) await req(t.objectStore('versions').put(v));
  });
  askToKeep();
  announce({ kind: 'changed', projectId: card.project.id });
}

/** When each card on this device last changed, by id. */
export async function cardDates(): Promise<Map<string, string>> {
  const rows = await tx(['projects'], 'readonly', (t) => req(t.objectStore('projects').getAll() as IDBRequest<ProjectRow[]>));
  return new Map(rows.map((p) => [p.id, p.updatedAt]));
}

/** How much this site keeps on the device, and how much the browser allows - when the browser says. */
export async function usage(): Promise<{ used: number; quota: number } | null> {
  try {
    const e = await navigator.storage?.estimate?.();
    return e?.usage !== undefined && e.quota !== undefined ? { used: e.usage, quota: e.quota } : null;
  } catch {
    return null;
  }
}
